import { classes } from 'typestyle';
import { MediaView, UserView } from 'common';
import { MediaType } from '@adalong/schemas';
import { MediaMark } from '@src/components/MediaInfo/MediaMark';
import { IMediaService } from '@src/types/Media';
import { PerfectScrollbar } from '@src/components/PerfectScrollbar';
import { Browsing } from '@src/types/Browsing';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { LanguageSearch } from '@src/fragments/Filters/managers/LanguageSearch';
import { SectionTitle } from '@src/components/SectionTitle';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { MarkType } from '@src/types/Media';
import {
  tabContentClass,
  contentFrameClass,
  infoSectionClass,
} from './styles';
import { SubtitleTranslationPanel } from './SubtitleTranslationPanel';

interface Props {
  media: MediaView
  mediaService: IMediaService
  enableAudioFilter: boolean
  user: UserView | null
  browsingService: Browsing
  onClose: () => void
  transcriptionLanguage?: string
  onTranscriptionLanguageChange?: (language: string) => void
}

export const InfoTab = (props: Props) => {
  const {
    media,
    mediaService,
    enableAudioFilter,
    transcriptionLanguage = 'eng',
    onTranscriptionLanguageChange,
  } = props;
  const marks = mediaService.getMarks(media, ['mentions', 'hashtags', 'tagged', 'labels', 'ownContent']);
  if (!enableAudioFilter) {
    return (
      <PerfectScrollbar className={classes(tabContentClass, contentFrameClass)}>
        <MediaMark marks={marks} />
      </PerfectScrollbar>
    );
  }

  const isAudioLabel = ({ type, text }: (typeof marks)[number]) => type === 'labels'
    && (text.startsWith('internal_voice')
      || text.startsWith('internal_music')
      || text.startsWith('internal_no_music_no_voice'));
  const audioMarks = marks.filter(isAudioLabel).map((mark) => ({ ...mark, symbol: VolumeUpIcon }));
  const sourceMarkTypes: MarkType[] = ['mentions', 'hashtags', 'tagged', 'ownContent'];
  const sourceMarks = marks.filter((mark) => sourceMarkTypes.includes(mark.type));
  const visualMarks = marks.filter((mark) => mark.type === 'labels' && !isAudioLabel(mark));
  const hasSubtitleTranslationPanel = media.type === MediaType.Video
    && Boolean(media._id && onTranscriptionLanguageChange);

  return (
    <>
      <PerfectScrollbar className={classes(tabContentClass, contentFrameClass)}>
        {sourceMarks.length ? (
          <section className={infoSectionClass} aria-labelledby="sources-section-title">
            <SectionTitle size="small">
              <span id="sources-section-title">{lang.sources}</span>
            </SectionTitle>
            <div>
              <MediaMark marks={sourceMarks} />
            </div>
          </section>
        ) : undefined}
        <section className={infoSectionClass} aria-labelledby="visual-labels-section-title">
          <SectionTitle size="small">
            <span id="visual-labels-section-title">{lang.visualLabels}</span>
          </SectionTitle>
          <div>
            <MediaMark marks={visualMarks} />
          </div>
        </section>
        {audioMarks.length ? (
          <section className={infoSectionClass} aria-labelledby="audio-labels-section-title">
            <SectionTitle size="small">
              <span id="audio-labels-section-title">{lang.audioLabels}</span>
            </SectionTitle>
            <div>
              <MediaMark marks={audioMarks} />
            </div>
          </section>
        ) : undefined}
        {media.audioLang ? (
          <section className={infoSectionClass} aria-labelledby="audio-language-section-title">
            <SectionTitle size="small">
              <span id="audio-language-section-title">{lang.audioLanguage}</span>
            </SectionTitle>
            <div>
              <MediaMark marks={[{
                type: 'labels',
                text: LanguageSearch.translateIso6393(media.audioLang),
                symbol: VolumeUpIcon,
              }]} />
            </div>
          </section>
        ) : undefined}
        {hasSubtitleTranslationPanel ? (
          <SubtitleTranslationPanel
            mediaId={media._id}
            selectedLanguage={transcriptionLanguage}
            onLanguageSelect={onTranscriptionLanguageChange}
            transcription={media.transcription}
          />
        ) : undefined}
        {media.transcription && !hasSubtitleTranslationPanel ? (
          <section className={infoSectionClass} aria-labelledby="transcription-section-title">
            <SectionTitle size="small">
              <span id="transcription-section-title">{lang.transcription}</span>
            </SectionTitle>
            <div>
              {media.transcription.replace(/\s+/g, ' ').trim()}
            </div>
          </section>
        ) : undefined}
      </PerfectScrollbar>
    </>
  );
};

const lang = LocalizedStrings({
  en: {
    sources: 'Sources',
    visualLabels: 'Visual labels',
    audioLabels: 'Audio labels',
    audioLanguage: 'Audio language',
    transcription: 'Transcription',
  },
});
